import { t } from 'i18next'
import { useSelector } from 'react-redux'
import PerfectScrollbar from 'react-perfect-scrollbar'
import styled from '@emotion/styled'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ReactComponent as FilterIcon } from '../../../../assets/icons/app/filter.icon.svg'
import { CatalogInterface } from '../../../../core/interface/rank/catalog.interface'
import { useAppDispatch } from '../../../../store/hooks'
import {
  selectAnalyticDetailsStateAnalyticDetailsBooksInputValues,
  selectAnalyticDetailsStateClilNameFilter,
  selectAnalyticDetailsStatePopularClilCatalogs,
} from '../../../../store/getters/analytics-details/analytic-details-v2.getters'
import { popularClilCatalogsAction } from '../../../../store/actions/analytics-details/popular-clil-catalogs.action'
import { analyticDetailsV2StateSetClilNameFilterAction } from '../../../../store/actions/analytics-details/analytic-details-v2.actions'
import { selectWebAppStateCatalogs } from '../../../../store/getters/web-app.getters'
import GlCatalog from '../../../share/GlCatalog'
import { webAppGetCatalogsAction } from '../../../../store/actions/web-app.actions'
import {
  ClilCatalogEnum,
  PopularCatalogEnum,
} from '../../../../core/enums/clil-catalog.enum'

const ActionButtonStyle = styled.button`
  overflow: hidden;
  color: #bebebe;
  text-align: left;
  font-variant-numeric: lining-nums proportional-nums;
  text-overflow: ellipsis;
  font-family: Fira Sans, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
  height: 40px;
  border: 1px #e9e9e9 solid;
  border-radius: 6px;
  width: 100%;
  padding-left: 8px;
  cursor: ${({ isSharingModeView }: { isSharingModeView: boolean }) =>
    (isSharingModeView && 'default') || 'pointer'};

  :hover {
    border-color: ${({ isSharingModeView }: { isSharingModeView: boolean }) =>
      (isSharingModeView && '#e9e9e9') || '#bebebe'};
  }

  :active {
    background-color: ${({
      isSharingModeView,
    }: {
      isSharingModeView: boolean
    }) => (isSharingModeView && '#fff') || '#bebebe33'};
  }
`

const ActionInputStyle = styled.input`
  background: none;
  border: none;
  outline: none;
  box-shadow: none;
  width: 100%;
  height: 100%;
`

const SearchCardAnchor = styled.div`
  height: 10px;
  padding-top: 10px;
`
const SearchCard = styled.div`
  background: #ffffff;
  box-shadow: 0px 2px 5px 0px #00000014;
  border-radius: 4px;
  max-height: 350px;
`

const SearchResultCard = styled.button`
  background: #ffffff;
  padding: 4px;
  width: 100%;
  border-radius: 4px;
`

const SearchResultIcon = styled.img`
  width: 20px;
  height: 20px;
  gap: 8px;
`
const SearchResultTitle = styled.p`
  padding: 0;
  margin: 0;
  font-family: Fira Sans, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  vertical-align: middle;
  font-variant-numeric-figure: lining-nums;
  font-variant-numeric-spacing: proportional-nums;
  color: #252525;
`

const SearchResultSubtitle = styled.p`
  padding: 0;
  margin: 0;
  font-family: Fira Sans, sans-serif;
  font-weight: 400;
  font-size: 10px;
  line-height: 14px;
  letter-spacing: 2%;
  vertical-align: middle;
  color: #666666;
`

const catalogNameMapping: Record<PopularCatalogEnum, ClilCatalogEnum> = {
  [PopularCatalogEnum.POPULAR_BOOKS]: ClilCatalogEnum.CLIL_POPULAR_BOOKS,
  [PopularCatalogEnum.ADO_YOUNG_ADULTS]: ClilCatalogEnum.CLIL_ADO_YOUNG_ADULTS,
  [PopularCatalogEnum.BIEN_ETRE_PRATIQUE]:
    ClilCatalogEnum.CLIL_BIEN_ETRE_PRATIQUE,
  [PopularCatalogEnum.ROMANS_FRANCOPHONES]:
    ClilCatalogEnum.CLIL_ROMANS_FRANCOPHONES,
  [PopularCatalogEnum.POLICIERS_THRILLERS]:
    ClilCatalogEnum.CLIL_POLICIERS_THRILLERS,
  [PopularCatalogEnum.COMICS]: ClilCatalogEnum.CLIL_COMICS,
  [PopularCatalogEnum.ROMANS_ETRANGERS]: ClilCatalogEnum.CLIL_ROMANS_ETRANGERS,
  [PopularCatalogEnum.MANGAS]: ClilCatalogEnum.CLIL_MANGAS,
  [PopularCatalogEnum.ROMANS]: ClilCatalogEnum.CLIL_ROMANS,
  [PopularCatalogEnum.JEUNESSE]: ClilCatalogEnum.CLIL_JEUNESSE,
  [PopularCatalogEnum.ROMANS_SENTIMENTAUX]:
    ClilCatalogEnum.CLIL_ROMANS_SENTIMENTAUX,
  [PopularCatalogEnum.ROMANS_GENRE]: ClilCatalogEnum.CLIL_ROMANS_GENRE,
  [PopularCatalogEnum.SHS]: ClilCatalogEnum.CLIL_SHS,
  [PopularCatalogEnum.BD]: ClilCatalogEnum.CLIL_BD,
}

const ClilFilterAction = ({
  isSharingModeView = false,
}: {
  isSharingModeView?: boolean
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isFilteringByClil, setIsFilteringByClil] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')
  const [isLoadingSelectedCatalog, setIsLoadingSelectedCatalog] =
    useState<boolean>(true)
  const [selectedCatalog, setSelectedCatalog] =
    useState<CatalogInterface | null>(null)
  const [isDisablingFilter, setIsDisablingFilter] = useState<boolean>(false)

  const clilFilterSearchInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const dispatch = useAppDispatch()
  const catalogs: CatalogInterface[] = useSelector(
    selectAnalyticDetailsStatePopularClilCatalogs,
  )
  const catalogsClil: CatalogInterface[] = useSelector(
    selectWebAppStateCatalogs,
  )

  const inputBooks = useSelector(
    selectAnalyticDetailsStateAnalyticDetailsBooksInputValues,
  )

  const clilNameFilter = useSelector(selectAnalyticDetailsStateClilNameFilter)

  useEffect(() => {
    if (!catalogs || catalogs.length === 0) {
      void dispatch(popularClilCatalogsAction())
    }
  }, [])

  useEffect(() => {
    if (!catalogsClil || catalogsClil.length === 0) {
      void dispatch(webAppGetCatalogsAction())
    }
  }, [])

  const mergedCatalogs = useMemo(() => {
    if (!catalogs || catalogs.length === 0) {
      return []
    }

    const enrichedCatalogs = catalogs.map((catalog) => {
      if (catalog.name === PopularCatalogEnum.POPULAR_BOOKS) {
        return {
          ...catalog,
          icon: 'https://storage.googleapis.com/icones.gleeph.net/01-litteraturegenerale.svg',
          icons: [
            {
              icon: 'https://storage.googleapis.com/icones.gleeph.net/01-litteraturegenerale.svg',
              color: '#B2B2B2',
            },
          ],
          name: ClilCatalogEnum.CLIL_POPULAR_BOOKS,
        }
      }

      const clilName = catalogNameMapping[catalog.name as PopularCatalogEnum]

      if (!clilName) {
        return catalog
      }

      const matchingClilCatalog = catalogsClil?.find(
        (clilCatalog) => clilCatalog.name === clilName,
      )

      if (!matchingClilCatalog) {
        return catalog
      }

      return {
        ...matchingClilCatalog,
        id: catalog.id,
        external_name: catalog.external_name,
      }
    })

    return enrichedCatalogs.sort((a, b) => {
      if (a.name === ClilCatalogEnum.CLIL_POPULAR_BOOKS) return -1
      if (b.name === ClilCatalogEnum.CLIL_POPULAR_BOOKS) return 1
      return a.name.localeCompare(b.name, 'fr')
    })
  }, [catalogs, catalogsClil])

  useEffect(() => {
    const defaultCatalog =
      mergedCatalogs.find(
        (catalog) => catalog.external_name?.external_name === 'popular_books',
      ) ?? null

    if (selectedCatalog === null && clilNameFilter === null && defaultCatalog) {
      setSelectedCatalog(defaultCatalog)
    } else {
      setSelectedCatalog(
        mergedCatalogs.find((catalog) => {
          const externalName = catalog.external_name?.external_name
          return externalName === clilNameFilter
        }) ?? null,
      )
    }

    setIsLoadingSelectedCatalog(false)
  }, [clilNameFilter, mergedCatalogs])

  const updateClilFilter = (clilName?: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (clilName && clilName.length) {
          next.set('popular_catalog_clil_name_filter', clilName)
        } else {
          next.delete('popular_catalog_clil_name_filter')
        }
        return next
      },
      { replace: true },
    )
  }

  useEffect(() => {
    const clilName = selectedCatalog?.external_name?.external_name ?? null

    if (
      !isLoadingSelectedCatalog &&
      mergedCatalogs.length > 0 &&
      (clilName !== null || isDisablingFilter) &&
      clilName !== clilNameFilter
    ) {
      if (isDisablingFilter) {
        setIsDisablingFilter(false)
      }

      updateClilFilter(clilName || undefined)

      void dispatch(
        analyticDetailsV2StateSetClilNameFilterAction({
          clilNameFilter: clilName,
        }),
      )
    }
  }, [
    selectedCatalog,
    clilNameFilter,
    isLoadingSelectedCatalog,
    isDisablingFilter,
  ])

  useEffect(() => {
    if (isFilteringByClil) {
      if (clilFilterSearchInputRef?.current) {
        clilFilterSearchInputRef.current.focus()
      }
    }
  }, [isFilteringByClil])

  if (isSharingModeView && selectedCatalog === null) {
    return null
  }

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      onBlur={(e) => {
        const related = e.relatedTarget

        if (!containerRef.current?.contains(related)) {
          setIsFilteringByClil(false)
        }
      }}
    >
      <ActionButtonStyle
        isSharingModeView={isSharingModeView}
        className={!selectedCatalog ? '' : 'bg-white'}
        disabled={inputBooks.length <= 0}
        aria-disabled={inputBooks.length <= 0}
        onClick={() => {
          if (isSharingModeView) return
          setIsFilteringByClil(true)
        }}
      >
        {isFilteringByClil ? (
          <ActionInputStyle
            ref={clilFilterSearchInputRef}
            onChange={(event) => setSearch(event.target.value)}
          />
        ) : !selectedCatalog ? (
          <p>{t('webApp.ranks.filters.byPopularCatalogClilName')}</p>
        ) : (
          <div className="flex flex-row items-center justify-start gap-2">
            <SearchResultIcon src={selectedCatalog.icon} alt="catalog icon" />
            <div className="text-left">
              <SearchResultTitle className="text-dark">
                {selectedCatalog.name}
              </SearchResultTitle>
              <SearchResultSubtitle>
                {selectedCatalog.type}
              </SearchResultSubtitle>
            </div>
          </div>
        )}
      </ActionButtonStyle>
      {isFilteringByClil && !isSharingModeView && (
        <SearchCardAnchor>
          <SearchCard>
            <PerfectScrollbar className="flex flex-col max-h-[350px]">
              {mergedCatalogs
                .filter(
                  (catalog) =>
                    search.length <= 0 ||
                    catalog.name
                      .toLocaleLowerCase()
                      .includes(search.toLocaleLowerCase()),
                )
                .map((catalog) => {
                  return (
                    <SearchResultCard
                      key={`catalog-${catalog.id}`}
                      className="flex flex-row justify-start gap-2"
                      onClick={() => {
                        setSelectedCatalog(catalog)
                        setIsFilteringByClil(false)
                      }}
                    >
                      <GlCatalog catalog={catalog} />
                    </SearchResultCard>
                  )
                })}
            </PerfectScrollbar>
          </SearchCard>
        </SearchCardAnchor>
      )}
    </div>
  )
}

export default ClilFilterAction
